import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import { isStateActive, navigateToState } from '@root/app/shared/navigation/navigation.service';
import {
    Badge,
    Box,
    Collapse,
    Drawer,
    IconButton,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import {
    BarChart,
    Bolt,
    Campaign,
    Chat,
    Close,
    Dashboard,
    Description,
    Dns,
    ExpandLess,
    ExpandMore,
    Forum,
    HelpOutline,
    MonitorHeart,
    People,
    Redeem,
    Settings,
    ShowChart,
    WarningAmber
} from '@mui/icons-material';
import { ConversationsResourceApi } from '@root/api';
import { authService } from '@root/app/shared/new-services/auth.service';
import { companyService } from '@root/app/shared/new-services/company.service';
import { rewardsService } from '@root/app/shared/new-services/rewards.service';
import useActionsEnabled from '@root/app/shared/hooks/useActionsEnabled';
import useRewardsEnabled from '@root/app/shared/hooks/useRewardsEnabled';
import { useTranslation } from '@root/app/shared/hooks/useTranslation';
import HfCompanySelector from '../company-selector/HfCompanySelector';
import { DRAWER_WIDTH, useSidebar } from '@root/app/shared/stores/sidebarStore';
import { HF_FONT_SIZES, HF_FONT_WEIGHTS, HF_SEMANTIC_COLORS } from '@root/app/shared/theme/designTokens';
import { FOCUS_CONFIRM_DEADLINE } from '@root/app/shared/utils/managementFocus.utils';

const JOURNEY_COMPANY_IDS = new Set(['5ed9f3beaa1caf00048f7eb4', '642ad601917fc82567e49b95']);
const PENDING_POLL_MS = 10000;

const drawerPaperSx = (theme: Theme) => ({
    width: DRAWER_WIDTH,
    boxSizing: 'border-box' as const,
    backgroundColor: theme.palette.hfLayout.sidebar,
    backgroundImage: 'none',
    color: theme.palette.hfLayout.navText,
    borderRight: 'none',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: '100vh'
});

const listItemSx = (active: boolean) => (theme: Theme) => ({
    py: 1.75,
    pl: 2.5,
    color: active ? theme.palette.common.white : theme.palette.hfLayout.navText,
    borderLeft: active ? `4px solid ${theme.palette.hfLayout.navActiveAccent}` : '4px solid transparent',
    backgroundColor: active ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        color: theme.palette.common.white
    },
    '& .MuiListItemIcon-root': {
        minWidth: 32,
        color: 'inherit'
    }
});

const nestedListItemSx = (active: boolean) => (theme: Theme) => ({
    py: 1,
    pl: 4,
    color: active ? theme.palette.common.white : theme.palette.hfLayout.navText,
    backgroundColor: active ? 'rgba(0, 0, 0, 0.35)' : theme.palette.hfLayout.navNested,
    '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        color: theme.palette.common.white
    }
});

const hasManagerAccess = (): boolean =>
    authService.userHasRole('MANAGER') || authService.userHasRole('ADMIN') || authService.userHasRole('ADVISOR');

const hasAdminOrAdvisorAccess = (): boolean =>
    authService.userHasRole('ADMIN') || authService.userHasRole('ADVISOR');

interface NavLeafItem {
    id: string;
    label: string;
    stateName: string;
    statePrefix: string;
    icon?: React.ReactNode;
    /** Small warning line under the label (e.g. a deadline). */
    caption?: string;
}

interface NavGroupItem {
    id: string;
    label: string;
    statePrefix: string;
    icon: React.ReactNode;
    children: NavLeafItem[];
    onHeaderClick?: () => void;
}

type MenuEntry =
    | { type: 'leaf'; item: NavLeafItem }
    | { type: 'group'; item: NavGroupItem }
    | { type: 'conversations' }
    | { type: 'actions' }
    | { type: 'rewards' };

const HfNavigationMenu: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const actionsEnabled = useActionsEnabled();
    const rewardsEnabled = useRewardsEnabled();
    const { isDesktop, isDrawerOpen, closeDrawer } = useSidebar();

    const location = useLocation();
    const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
    const [companyId, setCompanyId] = useState<string | undefined>();
    const [pendingConversations, setPendingConversations] = useState(0);
    const [pendingRedemptions, setPendingRedemptions] = useState(0);
    const [hideConversations, setHideConversations] = useState(false);
    const [timeRegistrationEnabled, setTimeRegistrationEnabled] = useState(false);

    const isActive = useCallback((stateName: string) => {
        return isStateActive(stateName, location.pathname);
    }, [location.pathname]);

    /** Conversations is a top-level item; it must not expand the Feedback submenu. */
    const isGroupActive = useCallback((groupId: string, statePrefix: string) => {
        if (groupId === 'feedback') {
            return isStateActive('feedback', location.pathname) && !isStateActive('feedback.conversations', location.pathname);
        }
        if (groupId === 'reports') {
            return isStateActive('reports', location.pathname) || isStateActive('journey', location.pathname);
        }
        return isStateActive(statePrefix, location.pathname);
    }, [location.pathname]);

    const showJourney = useMemo(() => (
        companyId
        && JOURNEY_COMPANY_IDS.has(companyId)
        && hasManagerAccess()
    ), [companyId]);

    const handleNavigate = useCallback((stateName: string) => {
        navigateToState(stateName);
        if (!isDesktop) {
            closeDrawer();
        }
    }, [closeDrawer, isDesktop]);

    const toggleGroup = (groupId: string) => {
        setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    };

    const loadPendingConversations = useCallback(() => {
        new ConversationsResourceApi()
            // Polled every PENDING_POLL_MS for as long as a tab is open, so a user on a
            // flaky connection reports a burst of identical transport failures for a badge
            // that simply keeps its previous number. Logged, not filed.
            .getPendingConversations({ backgroundPoll: true })
            .then((response) => {
                setPendingConversations(response.data?.pending ?? 0);
            })
            .catch(() => {
                // badge is non-critical; ignore polling errors
            });
    }, []);

    useEffect(() => {
        loadPendingConversations();
        const intervalId = window.setInterval(loadPendingConversations, PENDING_POLL_MS);
        return () => window.clearInterval(intervalId);
    }, [loadPendingConversations]);

    const loadPendingRedemptions = useCallback(() => {
        rewardsService.getPendingRedemptionsCount((err, count) => {
            if (!err && typeof count === 'number') {
                setPendingRedemptions(count);
            }
        }, { backgroundPoll: true });
    }, []);

    // Mirrors the conversations badge: poll only while the program is on.
    useEffect(() => {
        if (!rewardsEnabled || !hasAdminOrAdvisorAccess()) {
            setPendingRedemptions(0);
            return undefined;
        }
        loadPendingRedemptions();
        const intervalId = window.setInterval(loadPendingRedemptions, PENDING_POLL_MS);
        return () => window.clearInterval(intervalId);
    }, [rewardsEnabled, loadPendingRedemptions]);

    useEffect(() => {
        companyService.getUserCompany((err, company) => {
            if (!err && company?.id) {
                setCompanyId(company.id);
            }
        });

        // Hide the conversations section from managers when either the company
        // restricts conversations to admins, or the manager lacks the
        // CAN_ANSWER_CONVERSATIONS capability (a manager who can't reply to
        // conversations should not see the section at all). Evaluated as two
        // independent async inputs so a failure in one doesn't skip the other.
        let restrictedByCompany = false;
        let cannotAnswer = false;

        const applyHide = () => setHideConversations(restrictedByCompany || cannotAnswer);

        companyService.getConfigKey('restrictConversationsToAdmins', (err, restrictConversationsToAdmins) => {
            restrictedByCompany = !err && Boolean(restrictConversationsToAdmins) && authService.isMANAGER();
            applyHide();
        });

        authService.canAnswerConversations().then((canAnswer) => {
            cannotAnswer = !canAnswer;
            applyHide();
        });

        companyService.getConfigKey('timeRegistrationEnabled', (err, enabled) => {
            setTimeRegistrationEnabled(!err && Boolean(enabled));
        });
    }, []);

    useEffect(() => {
        const groupPrefixes: Record<string, string> = {
            feedback: 'feedback',
            reports: 'reports',
            manage: 'manage',
            advisors: 'advisors'
        };

        setExpandedGroups((prev) => {
            const next = { ...prev };
            let changed = false;

            Object.entries(groupPrefixes).forEach(([groupId, statePrefix]) => {
                let shouldExpand: boolean;
                if (groupId === 'feedback' && isActive('feedback.conversations')) {
                    shouldExpand = false;
                } else if (isGroupActive(groupId, statePrefix)) {
                    shouldExpand = true;
                } else {
                    shouldExpand = false;
                }

                if (next[groupId] !== shouldExpand) {
                    next[groupId] = shouldExpand;
                    changed = true;
                }
            });

            return changed ? next : prev;
        });
    }, [isActive, isGroupActive, location.pathname]);

    const menuEntries = useMemo((): MenuEntry[] => {
        const entries: MenuEntry[] = [
            {
                type: 'leaf',
                item: {
                    id: 'dashboard',
                    label: t('SIDEMENU_DASHBOARD'),
                    stateName: 'private.dashboard',
                    statePrefix: 'private.dashboard',
                    icon: <Dashboard fontSize='small' />
                }
            }
        ];

        if (showJourney) {
            entries.push({
                type: 'leaf',
                item: {
                    id: 'journey-lc',
                    label: t('SIDEMENU_JOURNEY'),
                    stateName: 'custom.lc',
                    statePrefix: 'custom.lc',
                    icon: <ShowChart fontSize='small' />
                }
            });
        }

        entries.push(
            {
                type: 'leaf',
                item: {
                    id: 'engagement',
                    label: t('SIDEMENU_ENGAGEMENT'),
                    stateName: 'engagement.screen',
                    statePrefix: 'engagement',
                    icon: <MonitorHeart fontSize='small' />
                }
            },
            {
                type: 'group',
                item: {
                    id: 'feedback',
                    label: t('SIDEMENU_FEEDBACK'),
                    statePrefix: 'feedback',
                    icon: <Chat fontSize='small' />,
                    onHeaderClick: () => handleNavigate('feedback.detail'),
                    children: [
                        {
                            id: 'feedback-comments',
                            label: t('COMMENTS'),
                            stateName: 'feedback.comments',
                            statePrefix: 'feedback.comment'
                        },
                        {
                            id: 'feedback-recognition',
                            label: t('SIDEMENU_RECOGNITION'),
                            stateName: 'feedback.recognition',
                            statePrefix: 'feedback.recognition'
                        },
                        {
                            id: 'feedback-ideas',
                            label: t('SIDEMENU_IDEAS'),
                            stateName: 'feedback.ideas',
                            statePrefix: 'feedback.ideas'
                        }
                    ]
                }
            }
        );

        if (!hideConversations) {
            entries.push({ type: 'conversations' });
        }

        if (actionsEnabled && hasManagerAccess()) {
            entries.push({ type: 'actions' });
        }

        const reportsChildren: NavLeafItem[] = [
            {
                id: 'reports-ranking',
                label: t('SIDEMENU_MANAGERS_RANKING'),
                stateName: 'reports.ranking',
                statePrefix: 'reports.ranking'
            },
            {
                id: 'reports-participation',
                label: t('SIDEMENU_PARTICIPATION'),
                stateName: 'reports.participation',
                statePrefix: 'reports.participation'
            },
            {
                id: 'reports-activation',
                label: t('SIDEMENU_PACE'),
                stateName: 'reports.activation',
                statePrefix: 'reports.activation'
            }
        ];

        if (authService.userHasRole('ADVISOR')) {
            reportsChildren.push({
                id: 'reports-journey',
                label: t('SIDEMENU_JOURNEY'),
                stateName: 'journey.screen',
                statePrefix: 'journey'
            });
        }

        entries.push({
            type: 'group',
            item: {
                id: 'reports',
                label: t('SIDEMENU_REPORTS'),
                statePrefix: 'reports',
                icon: <Description fontSize='small' />,
                children: reportsChildren
            }
        });

        entries.push(
            {
                type: 'leaf',
                item: {
                    id: 'surveys',
                    label: t('SIDEMENU_SURVEYS'),
                    stateName: 'surveys.list',
                    statePrefix: 'surveys',
                    icon: <Dns fontSize='small' />
                }
            },
            {
                type: 'leaf',
                item: {
                    id: 'topics',
                    label: t('SIDEMENU_CHECKINS'),
                    stateName: 'topics.browse',
                    statePrefix: 'topics',
                    icon: <HelpOutline fontSize='small' />
                }
            },
            {
                type: 'leaf',
                item: {
                    id: 'announcements',
                    label: t('SIDEMENU_ANNOUNCEMENTS'),
                    stateName: 'announcements.list',
                    statePrefix: 'announcements',
                    icon: <Campaign fontSize='small' />
                }
            }
        );

        if (rewardsEnabled && hasAdminOrAdvisorAccess()) {
            entries.push({ type: 'rewards' });
        }

        // Focos y roles sits right above People: it needs the company's attention until the draft is confirmed.
        if (hasAdminOrAdvisorAccess()) {
            entries.push({
                type: 'leaf',
                item: {
                    id: 'focus-roles',
                    label: t('SIDEMENU_FOCUS_ROLES'),
                    stateName: 'focus_roles',
                    statePrefix: 'focus_roles',
                    icon: <WarningAmber fontSize='small' sx={{ color: HF_SEMANTIC_COLORS.warning[500] }} />,
                    caption: t('SIDEMENU_FOCUS_ROLES_DEADLINE', { date: moment(FOCUS_CONFIRM_DEADLINE).format('D MMM') })
                }
            });
        }

        if (timeRegistrationEnabled && hasAdminOrAdvisorAccess()) {
            entries.push({
                type: 'group',
                item: {
                    id: 'manage',
                    label: t('SIDEMENU_MANAGE_USERS'),
                    statePrefix: 'manage',
                    icon: <People fontSize='small' />,
                    children: [
                        {
                            id: 'manage-users',
                            label: t('SIDEMENU_MANAGE_EMPLOYEES'),
                            stateName: 'manage.users',
                            statePrefix: 'manage.users'
                        },
                        {
                            id: 'manage-timetracking',
                            label: t('SIDEMENU_TIME_TRACKING'),
                            stateName: 'manage.timetracking',
                            statePrefix: 'manage.timetracking'
                        }
                    ]
                }
            });
        } else if (hasAdminOrAdvisorAccess()) {
            entries.push({
                type: 'leaf',
                item: {
                    id: 'manage-users-simple',
                    label: t('SIDEMENU_MANAGE_USERS'),
                    stateName: 'manage.users',
                    statePrefix: 'manage.users',
                    icon: <People fontSize='small' />
                }
            });
        }

        if (hasAdminOrAdvisorAccess()) {
            entries.push({
                type: 'leaf',
                item: {
                    id: 'company-config',
                    label: t('SIDEMENU_CONFIG'),
                    stateName: 'company.config',
                    statePrefix: 'company.config',
                    icon: <Settings fontSize='small' />
                }
            });
        }

        return entries;
    }, [
        actionsEnabled,
        rewardsEnabled,
        hideConversations,
        showJourney,
        t,
        timeRegistrationEnabled,
        handleNavigate
    ]);

    const advisorsGroup = useMemo((): NavGroupItem | null => {
        if (!authService.userHasRole('ADVISOR')) {
            return null;
        }

        return {
            id: 'advisors',
            label: t('SIDEMENU_ADVISORS'),
            statePrefix: 'advisors',
            icon: <BarChart fontSize='small' />,
            onHeaderClick: () => handleNavigate('advisors.employee_locator'),
            children: [
                {
                    id: 'advisors-employees',
                    label: t('SIDEMENU_EMPLOYEES'),
                    stateName: 'advisors.employee_locator',
                    statePrefix: 'advisors.employee_locator'
                },
                {
                    id: 'advisors-surveys',
                    label: t('SURVEY_TEMPLATES_SECTION_TITLE'),
                    stateName: 'advisors.surveys',
                    statePrefix: 'advisors.surveys'
                },
                {
                    id: 'advisors-templates',
                    label: t('SIDE_MENU_ANNOUNCEMENT_TEMPLATES'),
                    stateName: 'advisors.templates',
                    statePrefix: 'advisors.templates'
                },
                ...(authService.isPureAdvisor() ? [{
                    id: 'advisors-management',
                    label: t('SIDEMENU_ADVISORS_MANAGEMENT'),
                    stateName: 'advisors.management',
                    statePrefix: 'advisors.management'
                }] : []),
                {
                    id: 'advisors-company-create',
                    label: t('SIDEMENU_COMPANY_CREATE'),
                    stateName: 'advisors.company_create',
                    statePrefix: 'advisors.company_create'
                },
                {
                    id: 'advisors-absences',
                    label: t('SIDEMENU_ABSENCES'),
                    stateName: 'advisors.absences',
                    statePrefix: 'advisors.absences'
                },
                {
                    id: 'advisors-design-system',
                    label: 'Design System',
                    stateName: 'design.system',
                    statePrefix: 'design.system'
                }
            ]
        };
    }, [handleNavigate, t]);

    const renderLeaf = (item: NavLeafItem, nested = false) => (
        <ListItemButton
            key={item.id}
            onClick={() => handleNavigate(item.stateName)}
            sx={nested ? nestedListItemSx(isActive(item.statePrefix)) : listItemSx(isActive(item.statePrefix))}
        >
            {!nested && item.icon && (
                <ListItemIcon>{item.icon}</ListItemIcon>
            )}
            <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                    fontSize: nested ? HF_FONT_SIZES.bodySm : HF_FONT_SIZES.small,
                    fontWeight: HF_FONT_WEIGHTS.semibold,
                    noWrap: true
                }}
                secondary={item.caption}
                secondaryTypographyProps={{
                    fontSize: HF_FONT_SIZES.mini,
                    color: HF_SEMANTIC_COLORS.warning[500],
                    noWrap: true
                }}
            />
        </ListItemButton>
    );

    const renderGroup = (group: NavGroupItem) => {
        const groupActive = isGroupActive(group.id, group.statePrefix);
        const isExpanded = expandedGroups[group.id] ?? groupActive;

        return (
            <Box key={group.id}>
                <ListItemButton
                    onClick={() => {
                        toggleGroup(group.id);
                        group.onHeaderClick?.();
                    }}
                    sx={listItemSx(groupActive)}
                >
                    <ListItemIcon>{group.icon}</ListItemIcon>
                    <ListItemText
                        primary={group.label}
                        primaryTypographyProps={{ fontSize: HF_FONT_SIZES.small, fontWeight: HF_FONT_WEIGHTS.semibold, noWrap: true }}
                    />
                    {isExpanded ? <ExpandLess fontSize='small' /> : <ExpandMore fontSize='small' />}
                </ListItemButton>
                <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                    <List
                        component='div'
                        disablePadding
                        sx={{ backgroundColor: theme.palette.hfLayout.navNested }}
                    >
                        {group.children.map((child) => renderLeaf(child, true))}
                    </List>
                </Collapse>
            </Box>
        );
    };

    const renderEntry = (entry: MenuEntry, index: number) => {
        if (entry.type === 'leaf') {
            return renderLeaf(entry.item);
        }

        if (entry.type === 'group') {
            return renderGroup(entry.item);
        }

        if (entry.type === 'conversations') {
            return (
                <ListItemButton
                    key={`conversations-${index}`}
                    onClick={() => {
                        setExpandedGroups((prev) => ({ ...prev, feedback: false }));
                        handleNavigate('feedback.conversations');
                    }}
                    sx={listItemSx(isActive('feedback.conversations'))}
                >
                    <ListItemIcon>
                        <Badge
                            badgeContent={pendingConversations > 0 ? pendingConversations : undefined}
                            color='error'
                            overlap='circular'
                        >
                            <Forum fontSize='small' />
                        </Badge>
                    </ListItemIcon>
                    <ListItemText
                        primary={t('SIDEMENU_CONVERSATIONS')}
                        primaryTypographyProps={{ fontSize: HF_FONT_SIZES.small, fontWeight: HF_FONT_WEIGHTS.semibold, noWrap: true }}
                    />
                </ListItemButton>
            );
        }

        if (entry.type === 'rewards') {
            return (
                <ListItemButton
                    key={`rewards-${index}`}
                    onClick={() => handleNavigate('rewards.home')}
                    sx={listItemSx(isActive('rewards'))}
                >
                    <ListItemIcon>
                        <Badge
                            badgeContent={pendingRedemptions > 0 ? pendingRedemptions : undefined}
                            color='error'
                            overlap='circular'
                        >
                            <Redeem fontSize='small' />
                        </Badge>
                    </ListItemIcon>
                    <ListItemText
                        primary={t('SIDEMENU_REWARDS')}
                        primaryTypographyProps={{ fontSize: HF_FONT_SIZES.small, fontWeight: HF_FONT_WEIGHTS.semibold, noWrap: true }}
                    />
                </ListItemButton>
            );
        }

        return (
            <ListItemButton
                key={`actions-${index}`}
                onClick={() => handleNavigate('actions.list')}
                sx={listItemSx(isActive('actions'))}
            >
                <ListItemIcon><Bolt fontSize='small' /></ListItemIcon>
                <ListItemText
                    primary={t('SIDEMENU_ACTIONS')}
                    primaryTypographyProps={{ fontSize: HF_FONT_SIZES.small, fontWeight: HF_FONT_WEIGHTS.semibold, noWrap: true }}
                />
            </ListItemButton>
        );
    };

    return (
        <Drawer
            variant={isDesktop ? 'persistent' : 'temporary'}
            open={isDrawerOpen}
            onClose={closeDrawer}
            PaperProps={{ elevation: 0 }}
            ModalProps={{ keepMounted: true }}
            sx={{
                width: isDrawerOpen ? DRAWER_WIDTH : 0,
                flexShrink: 0,
                zIndex: (theme) => theme.zIndex.drawer,
                transition: (theme) => theme.transitions.create('width', {
                    easing: theme.transitions.easing.sharp,
                    duration: theme.transitions.duration.enteringScreen
                }),
                '& .MuiDrawer-paper': drawerPaperSx(theme)
            }}
        >
            <Box
                sx={{
                    px: 2,
                    py: 2.5,
                    textAlign: 'center',
                    position: 'relative',
                    flexShrink: 0,
                    backgroundColor: theme.palette.hfLayout.sidebar
                }}
            >
                {!isDesktop && (
                    <IconButton
                        onClick={closeDrawer}
                        aria-label='close navigation'
                        sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            color: theme.palette.hfLayout.navText
                        }}
                    >
                        <Close fontSize='small' />
                    </IconButton>
                )}
                <HfCompanySelector />
            </Box>

            <List
                component='nav'
                disablePadding
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    py: 0,
                    backgroundColor: theme.palette.hfLayout.sidebar,
                    color: theme.palette.hfLayout.navText,
                    '& .MuiListItemButton-root': {
                        color: 'inherit'
                    },
                    '& .MuiListItemText-primary': {
                        color: 'inherit'
                    }
                }}
            >
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {menuEntries.map((entry, index) => renderEntry(entry, index))}
                </Box>
                {advisorsGroup && (
                    <Box sx={{ mt: 'auto', flexShrink: 0 }}>
                        {renderGroup(advisorsGroup)}
                    </Box>
                )}
            </List>
        </Drawer>
    );
};

export default HfNavigationMenu;
